import React, { useState } from 'react';

const formatVal = (val) => {
  if (val == null || isNaN(val)) return '—';
  if (val === 0) return '0';
  const str = val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  if (val < 0) return <span className="text-[#c53030]">{str}</span>;
  return str;
};

const CompareMonthsTableRow = ({ node, isExpanded, allMonths, latestMonth, depth = 0 }) => {
  const [expanded, setExpanded] = useState(isExpanded);
  
  React.useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  const hasChildren = node.children && node.children.length > 0;
  
  return (
    <>
      <tr className={`border-b border-slate-200 transition-colors ${hasChildren ? 'bg-[#f4f7fc] font-bold text-[#1e293b]' : 'bg-white text-slate-700'}`}>
        <td className="p-3 text-[14px] flex items-center gap-1">
          <span style={{ paddingLeft: `${depth * 1.5}rem` }}></span>
          {hasChildren ? (
            <span className="text-[#3b82f6] text-[18px] leading-[0] font-bold mx-1 cursor-pointer" onClick={() => setExpanded(!expanded)}>•</span>
          ) : (
            <span className="w-4 mx-1"></span>
          )}
          <span className={hasChildren ? "cursor-pointer" : ""} onClick={() => hasChildren && setExpanded(!expanded)}>{node.description}</span>
        </td>
        {allMonths.map(m => (
          <td key={m} className="p-3 text-[14px] text-right font-medium">
            {formatVal(node.months[m])}
          </td>
        ))}
        <td className="p-3 text-[14px] text-right font-medium">
          {node.mom != null ? (
            <span className={node.mom > 0 ? 'text-green-600' : node.mom < 0 ? 'text-[#c53030]' : 'text-slate-500'}>
              {node.mom > 0 ? '▲ ' : node.mom < 0 ? '▼ ' : ''}
              {Math.abs(node.mom).toFixed(1)}%
            </span>
          ) : '—'}
        </td>
      </tr>
      {expanded && hasChildren && node.children.map(child => (
        <CompareMonthsTableRow key={child.id} node={child} isExpanded={isExpanded} allMonths={allMonths} latestMonth={latestMonth} depth={depth + 1} />
      ))}
    </>
  );
};

const CompareMonthsTable = ({ data = [] }) => {
  // 1. Get unique months
  const allMonthsSet = new Set();
  data.forEach(d => {
    if (d.Month_Year) allMonthsSet.add(d.Month_Year);
  });
  
  const allMonths = Array.from(allMonthsSet).sort((a, b) => {
    const monthOrder = { "Jan": 1, "Feb": 2, "Mar": 3, "Apr": 4, "May": 5, "Jun": 6, "Jul": 7, "Aug": 8, "Sep": 9, "Oct": 10, "Nov": 11, "Dec": 12 };
    const getVal = (my) => {
       const [m, y] = (my || '').split('-');
       return (parseInt(y) * 12) + (monthOrder[m] || 0);
    };
    return getVal(a) - getVal(b);
  });
  
  const latestMonth = allMonths[allMonths.length - 1];
  const previousMonth = allMonths[allMonths.length - 2];
  
  // 2. Build map and tree
  const map = {};
  const tree = [];
  let gt = { description: 'Grand Total', months: {}, mom: null };
  
  data.forEach(row => {
    if (row.Description === 'Grand Total') {
      if (row.Month_Year) {
         gt.months[row.Month_Year] = row.Grand_Total !== undefined ? row.Grand_Total : row.Monthly_Actuals_Grand_Total;
         if (row.Month_Year === latestMonth && row.MoM_Percentage !== undefined) {
            gt.mom = row.MoM_Percentage;
         }
      }
      return;
    }
    
    const srNo = row.Sr_No || row.S_No;
    if (srNo == null) return;
    
    if (!map[srNo]) {
      map[srNo] = {
        id: srNo,
        description: row.Description,
        months: {},
        mom: null,
        children: []
      };
    }
    
    const node = map[srNo];
    if (row.Month_Year) {
      node.months[row.Month_Year] = row.Grand_Total !== undefined ? row.Grand_Total : row.Monthly_Actuals_Grand_Total;
      if (row.Month_Year === latestMonth && row.MoM_Percentage !== undefined) {
         node.mom = row.MoM_Percentage;
      }
    }
  });
  
  Object.values(map).forEach(node => {
    if (node.id && String(node.id).includes('.')) {
      const parentId = String(node.id).substring(0, String(node.id).lastIndexOf('.'));
      if (map[parentId]) {
        map[parentId].children.push(node);
      } else {
        tree.push(node);
      }
    } else {
      tree.push(node);
    }
  });

  return (
    <div className="mt-8 bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#f8fafc] border-b border-slate-200">
              <th className="p-3 text-[13px] font-bold text-slate-600 tracking-wide">Description</th>
              {allMonths.map(m => (
                <th key={m} className="p-3 text-[13px] font-bold text-slate-600 text-right tracking-wide">{m}</th>
              ))}
              <th className="p-3 text-[13px] font-bold text-slate-600 text-right tracking-wide">
                {latestMonth && previousMonth ? `MoM Δ (${previousMonth} → ${latestMonth})` : 'MoM %'}
              </th>
            </tr>
          </thead>
          <tbody>
            {tree.map(node => (
              <CompareMonthsTableRow key={node.id} node={node} isExpanded={true} allMonths={allMonths} latestMonth={latestMonth} />
            ))}
            {tree.length > 0 && (
              <tr className="bg-[#f4f7fc] font-bold border-t-2 border-slate-300 text-[#1e293b]">
                <td className="p-3 text-[15px]">Grand Total</td>
                {allMonths.map(m => (
                  <td key={m} className="p-3 text-[15px] text-right font-medium">{formatVal(gt.months[m])}</td>
                ))}
                <td className="p-3 text-[15px] text-right font-medium">
                  {gt.mom != null ? (
                    <span className={gt.mom > 0 ? 'text-green-600' : gt.mom < 0 ? 'text-[#c53030]' : 'text-slate-500'}>
                      {gt.mom > 0 ? '▲ ' : gt.mom < 0 ? '▼ ' : ''}
                      {Math.abs(gt.mom).toFixed(1)}%
                    </span>
                  ) : '—'}
                </td>
              </tr>
            )}
            {tree.length === 0 && (
              <tr>
                <td colSpan={allMonths.length + 2} className="p-6 text-center text-slate-500">No data available for comparison.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompareMonthsTable;
